import Add from '@mui/icons-material/Add';
import AddCommentOutlined from '@mui/icons-material/AddCommentOutlined';
import AddOutlined from '@mui/icons-material/AddOutlined';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import Lock from '@mui/icons-material/Lock';
import MoreVert from '@mui/icons-material/MoreVert';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { cloneElement, ReactElement, RefObject, useMemo, useRef, useState } from 'react';

import Menu, { MenuHandler } from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { Badge } from '~components/Tabs/StatusTabs';
import { TreadAgentSkillBasic } from '~hooks/useTreadAgent/models';
import { SearchInput } from '~pages/Dispatch/components/drivers/Filters/SearchInput';
import TreadAgentLoader from '~tread-agent/TreadAgentLoader';
import TreadAgentSkillDeleteDialog from '~tread-agent/TreadAgentSkillDeleteDialog';
import TreadAgentSkillsColumn from '~tread-agent/TreadAgentSkillsColumn';
import { filterTreadAgentSkillsBySearch } from '~tread-agent/treadAgentUtils';

interface TreadAgentSkillsPageProps {
  canCreate: boolean;
  canDelete: boolean;
  canEdit: boolean;
  companyId: string;
  isLoading: boolean;
  onCreate: () => void;
  onCreateWithAgent: () => void;
  onOpen: (skill: TreadAgentSkillBasic) => void;
  onUse: (skill: TreadAgentSkillBasic) => void;
  skills: TreadAgentSkillBasic[];
}

interface SkillMenuItemProps {
  icon: ReactElement<{ sx?: object }>;
  label: string;
  menuHandler: RefObject<MenuHandler>;
  onClick: () => void;
}

function SkillMenuItem({ icon, label, menuHandler, onClick }: SkillMenuItemProps) {
  const theme = useTheme();

  return (
    <MenuItem
      onClick={(event) => {
        // The menu renders in a portal, but events still bubble to the clickable row.
        event.stopPropagation();
        menuHandler.current?.onClose?.();
        onClick();
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, width: '100%' }}>
        {cloneElement(icon, {
          sx: { color: theme.brandV2.colors.treadBlack, fontSize: '16px' },
        })}

        <Typography sx={{ color: theme.brandV2.colors.treadBlack, fontSize: '12px' }}>
          {label}
        </Typography>
      </Box>
    </MenuItem>
  );
}

interface SkillRowMenuProps {
  canDelete: boolean;
  canEdit: boolean;
  onDelete: (skill: TreadAgentSkillBasic) => void;
  onOpen: (skill: TreadAgentSkillBasic) => void;
  onUse: (skill: TreadAgentSkillBasic) => void;
  skill: TreadAgentSkillBasic;
}

function SkillRowMenu({
  canDelete,
  canEdit,
  onDelete,
  onOpen,
  onUse,
  skill,
}: SkillRowMenuProps) {
  const menuHandler = useRef<MenuHandler>(null);
  const isEditable = !skill.isSystem && canEdit;
  const isDeletable = !skill.isSystem && canDelete;

  return (
    <Menu
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      menuTrigger={
        <IconButton
          aria-label={t('tread_agent.skills.actions_for', { name: skill.name })}
          size="small"
        >
          <MoreVert sx={{ fontSize: 18 }} />
        </IconButton>
      }
      ref={menuHandler}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
    >
      <SkillMenuItem
        icon={<AddCommentOutlined />}
        label={t('tread_agent.skills.use')}
        menuHandler={menuHandler}
        onClick={() => onUse(skill)}
      />

      {isEditable && (
        <SkillMenuItem
          icon={<EditOutlined />}
          label={t('actions.edit')}
          menuHandler={menuHandler}
          onClick={() => onOpen(skill)}
        />
      )}

      {isDeletable && <Divider sx={{ mx: 1 }} />}

      {isDeletable && (
        <SkillMenuItem
          icon={<DeleteOutlineOutlined />}
          label={t('actions.delete')}
          menuHandler={menuHandler}
          onClick={() => onDelete(skill)}
        />
      )}
    </Menu>
  );
}

interface SkillSectionProps extends Omit<SkillRowMenuProps, 'skill'> {
  emptyMessage?: string;
  skills: TreadAgentSkillBasic[];
  title: string;
}

function SkillSection({
  canDelete,
  canEdit,
  emptyMessage,
  onDelete,
  onOpen,
  onUse,
  skills,
  title,
}: SkillSectionProps) {
  const theme = useTheme();

  if (skills.length === 0 && !emptyMessage) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 1 }}>
        <Typography sx={{ fontWeight: 600 }} variant="subtitle1">
          {title}
        </Typography>

        <Badge content={String(skills.length)} />
      </Box>

      {skills.length === 0 && (
        <Typography color="text.secondary" variant="body2">
          {emptyMessage}
        </Typography>
      )}

      {skills.map((skill) => (
        <Box
          key={skill.name}
          onClick={() => onOpen(skill)}
          sx={{
            alignItems: 'center',
            borderRadius: theme.brandV2.borderRadius,
            cursor: 'pointer',
            display: 'flex',
            gap: 1.5,
            mb: 0.5,
            mx: -1,
            p: 1,
            '&:hover': { backgroundColor: theme.brandV2.colors.treadGray8 },
          }}
        >
          <LayersOutlined color="action" fontSize="small" />

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
              <Typography noWrap sx={{ fontWeight: 500 }} variant="body2">
                {skill.name}
              </Typography>

              {skill.isSystem && (
                <Lock
                  aria-label={t('tread_agent.skills.built_in_label')}
                  color="action"
                  sx={{ fontSize: 13 }}
                />
              )}
            </Box>

            <Typography color="text.secondary" noWrap variant="body2">
              {skill.description}
            </Typography>
          </Box>

          <SkillRowMenu
            canDelete={canDelete}
            canEdit={canEdit}
            onDelete={onDelete}
            onOpen={onOpen}
            onUse={onUse}
            skill={skill}
          />
        </Box>
      ))}
    </Box>
  );
}

export default function TreadAgentSkillsPage({
  canCreate,
  canDelete,
  canEdit,
  companyId,
  isLoading,
  onCreate,
  onCreateWithAgent,
  onOpen,
  onUse,
  skills,
}: TreadAgentSkillsPageProps) {
  const [search, setSearch] = useState('');
  const addMenuHandler = useRef<MenuHandler>(null);
  const [skillToDelete, setSkillToDelete] = useState<TreadAgentSkillBasic | null>(null);

  const matchingSkills = useMemo(
    () => filterTreadAgentSkillsBySearch(skills, search),
    [skills, search],
  );
  const customSkills = useMemo(
    () => matchingSkills.filter((skill) => !skill.isSystem),
    [matchingSkills],
  );
  const systemSkills = useMemo(
    () => matchingSkills.filter((skill) => skill.isSystem),
    [matchingSkills],
  );
  const isSearching = search.trim() !== '';

  return (
    <TreadAgentSkillsColumn>
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 600 }} variant="h5">
            {t('tread_agent.skills.title')}
          </Typography>

          <Typography color="text.secondary" variant="body2">
            {t('tread_agent.skills.description_intro')}
          </Typography>
        </Box>

        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, mb: 3 }}>
          <Box sx={{ flexGrow: 1 }}>
            <SearchInput
              onChange={setSearch}
              placeholder={t('tread_agent.skills.search_placeholder')}
              value={search}
            />
          </Box>

          {canCreate && (
            <Menu
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              menuTrigger={
                <SmallButton
                  color="brandV2Black"
                  endIcon={<ArrowDropDown sx={{ fontSize: 18 }} />}
                  startIcon={<Add sx={{ fontSize: 16 }} />}
                  sx={{
                    borderColor: 'divider',
                    color: 'text.primary',
                    flexShrink: 0,
                    '&.MuiButtonBase-root': { px: 1 },
                    '& .MuiButton-endIcon': { ml: 0.25, mr: 0 },
                    '& .MuiButton-startIcon': { ml: 0, mr: 0.25 },
                  }}
                  variant="outlined"
                >
                  {t('tread_agent.skills.add')}
                </SmallButton>
              }
              ref={addMenuHandler}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
              <SkillMenuItem
                icon={<AddOutlined />}
                label={t('tread_agent.skills.new_skill')}
                menuHandler={addMenuHandler}
                onClick={onCreate}
              />

              <SkillMenuItem
                icon={<AutoAwesomeOutlined />}
                label={t('tread_agent.skills.create_with_agent')}
                menuHandler={addMenuHandler}
                onClick={onCreateWithAgent}
              />
            </Menu>
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <TreadAgentLoader size={24} />
          </Box>
        ) : (
          <>
            <SkillSection
              emptyMessage={
                isSearching ? undefined : t('tread_agent.skills.no_custom_skills')
              }
              canDelete={canDelete}
              canEdit={canEdit}
              onDelete={setSkillToDelete}
              onOpen={onOpen}
              onUse={onUse}
              skills={customSkills}
              title={t('tread_agent.skills.your_skills')}
            />

            <SkillSection
              canDelete={canDelete}
              canEdit={canEdit}
              onDelete={setSkillToDelete}
              onOpen={onOpen}
              onUse={onUse}
              skills={systemSkills}
              title={t('tread_agent.skills.tread_skills')}
            />

            {isSearching && matchingSkills.length === 0 && (
              <Typography variant="body2">
                {t('tread_agent.skills.no_matching_skills')}
              </Typography>
            )}
          </>
        )}
      </Box>

      <TreadAgentSkillDeleteDialog
        companyId={companyId}
        onClose={() => setSkillToDelete(null)}
        skill={skillToDelete}
      />
    </TreadAgentSkillsColumn>
  );
}
